import React from 'react';
import { useTranslation } from 'react-i18next';
import { Crown, Check, Sparkles, Star } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuthStore } from '../../store/authStore';

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export function SubscriptionInfo() {
  const { t } = useTranslation();
  const { user } = useAuthStore();

  const premiumFeatures = [
    {
      text: t('subscription.features.ai'),
      highlight: true
    },
    {
      text: t('subscription.features.challenges'),
      highlight: true
    },
    {
      text: t('subscription.features.stats'),
      highlight: false
    },
    {
      text: t('subscription.features.reminders'),
      highlight: false
    },
    {
      text: t('subscription.features.ads'),
      highlight: true
    }
  ];

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-1">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center">
          {t('subscription.title')}
          <Sparkles className="w-4 h-4 ml-2 text-yellow-500 dark:text-yellow-400" />
        </h3>
      </div>

      {/* Main Card */}
      <div className="relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-500/10 to-indigo-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-purple-500/10 to-pink-500/10 rounded-full blur-3xl" />

        <motion.div
          className="relative bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200/50 dark:border-gray-700/50 shadow-lg"
          whileHover={{ y: -2 }}
          transition={{ duration: 0.2 }}
        >
          {/* Plan Header */}
          <div className="flex items-start space-x-3 mb-4">
            <div className="relative flex-shrink-0 mt-1">
              <Crown className="w-6 h-6 text-yellow-500 dark:text-yellow-400" />
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 1, 0.5]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                }}
                className="absolute inset-0 bg-yellow-500/20 rounded-full blur-md"
              />
            </div>
            <div className="min-w-0 flex-1">
              <h4 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent truncate">
                {user?.isPremium
                  ? t('subscription.plans.premium')
                  : t('subscription.plans.free')}
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {user?.isPremium
                  ? t('subscription.plans.free')
                  : t('subscription.plans.premium')}
              </p>
            </div>
          </div>

          {/* Features List */}
          <motion.ul
            variants={container}
            initial="hidden"
            animate="show"
            className="space-y-2.5"
          >
            {premiumFeatures.map((feature, index) => (
              <motion.li
                key={index}
                variants={item}
                className="flex items-center space-x-2"
              >
                <div className={`p-1 rounded-full flex-shrink-0 ${user?.isPremium
                    ? 'bg-green-100 dark:bg-green-900/30'
                    : 'bg-gray-100 dark:bg-gray-800'
                  }`}>
                  <Check className={`w-3.5 h-3.5 ${user?.isPremium
                      ? 'text-green-500 dark:text-green-400'
                      : 'text-gray-400 dark:text-gray-600'
                    }`} />
                </div>
                <span className={`text-sm line-clamp-1 flex-1 ${user?.isPremium
                    ? 'text-gray-900 dark:text-gray-100'
                    : 'text-gray-500 dark:text-gray-400'
                  } ${feature.highlight ? 'font-medium' : ''}`}>
                  {feature.text}
                  {feature.highlight && !user?.isPremium && (
                    <Star className="w-3 h-3 text-yellow-500 inline ml-1 flex-shrink-0" />
                  )}
                </span>
              </motion.li>
            ))}
          </motion.ul>

          {/* Upgrade Button */}
          {!user?.isPremium && (
            <motion.button
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className="mt-4 w-full bg-gradient-to-r from-blue-500 to-indigo-600 
                dark:from-blue-600 dark:to-indigo-700 text-white py-2.5 px-4 
                rounded-lg text-sm font-medium shadow-md shadow-blue-500/10 
                hover:shadow-blue-500/20 transition-all
                flex items-center justify-center space-x-2"
            >
              <Crown className="w-4 h-4" />
              <span className="truncate">{t('subscription.upgrade')}</span>
            </motion.button>
          )}
        </motion.div>
      </div>
    </motion.section>
  );
}

export default SubscriptionInfo;